import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { swalError, swalLoading, swalSuccess } from "@/utils/lib/swal";
import { SubmitButton } from "@/components/ui/button";
import {
  ProvincePayloadSchema,
  provincePayloadSchema,
  useProvince,
  useProvinceEdit,
} from "@/api/province";
import { Loading } from "@/components/ui/loading";
import { ProvinceFormFields } from "./ProvinceFormFields";
import { FormContainer } from "@/components/form/form-container";
import { Anchor } from "@/components/ui/anchor";
import { ErrorMessageFromResponse } from "@/api/types";

export function ProvinceEditForm({ id }: { id: string }) {
  const { data: province, isPending: isLoadingData } = useProvince({
    id,
  });
  const form = useForm<ProvincePayloadSchema>({
    resolver: zodResolver(provincePayloadSchema),
    defaultValues: {
      provinceCode: province?.provinceCode,
      provinceName: province?.provinceName,
    },
    values: province,
  });
  const { mutate, isPending } = useProvinceEdit();
  const navigate = useNavigate();

  const handleSubmit = (values: ProvincePayloadSchema) => {
    swalLoading.fire();
    mutate(
      { id: id, data: values },
      {
        onSuccess: () => {
          swalSuccess
            .fire({
              text: "Province has been updated",
            })
            .then(() => navigate("/master-data/province"));
        },
        onError: (error) => {
          swalError.fire({
            text:
              (error as ErrorMessageFromResponse).response?.data.data ??
              error.message,
          });
        },
      },
    );
  };

  if (isLoadingData) {
    return <Loading />;
  }

  return (
    <FormContainer form={form} onSubmit={handleSubmit}>
      <ProvinceFormFields />
      <div className="ml-auto flex gap-x-2">
        <Anchor href="/master-data/province" mood="axiata-blue-outline">
          Cancel
        </Anchor>
        <SubmitButton loading={isPending} />
      </div>
    </FormContainer>
  );
}
